import { 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc, 
  doc, 
  addDoc, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from './authService';

interface DeletedUserData {
  originalUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  profilePictureURL?: string;
  role?: string;
  systemRole?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  createdAt?: Timestamp;
  deletedAt: Timestamp;
  deletedBy: string;
  deletedByName: string;
  deletionReason?: string;
  relatedDataDeleted: {
    transactions: number;
    staffEntries: number;
    otpVerifications: number;
    sentGiftCards: number;
    receivedGiftCards: number;
    creditActivities: number;
  };
}

export const userDeletionService = {
  async softDeleteUser(userId: string, userData: any, deletionReason?: string): Promise<void> {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      console.log(`Starting soft delete process for user: ${userId}`);

      // Count and collect data to be deleted
      const deletionStats = {
        transactions: 0,
        staffEntries: 0,
        otpVerifications: 0,
        sentGiftCards: 0,
        receivedGiftCards: 0,
        creditActivities: 0
      };

      // Delete transactions where customerId matches userId
      const transactionsQuery = query(
        collection(db, 'transactions'),
        where('customerId', '==', userId)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      console.log(`Found ${transactionsSnapshot.size} transactions to delete`);
      
      for (const transactionDoc of transactionsSnapshot.docs) {
        await deleteDoc(transactionDoc.ref);
        deletionStats.transactions++;
      }

      // Delete staff entries where userId matches
      const staffQuery = query(
        collection(db, 'staff'),
        where('userId', '==', userId)
      );
      const staffSnapshot = await getDocs(staffQuery);
      console.log(`Found ${staffSnapshot.size} staff entries to delete`);
      
      for (const staffDoc of staffSnapshot.docs) {
        await deleteDoc(staffDoc.ref);
        deletionStats.staffEntries++;
      }

      // Delete OTP verifications where userId matches
      const otpQuery = query(
        collection(db, 'otpVerification'),
        where('userId', '==', userId)
      );
      const otpSnapshot = await getDocs(otpQuery);
      console.log(`Found ${otpSnapshot.size} OTP verifications to delete`);
      
      for (const otpDoc of otpSnapshot.docs) {
        await deleteDoc(otpDoc.ref);
        deletionStats.otpVerifications++;
      }

      // Delete gift cards where senderUserId matches
      const sentGiftCardsQuery = query(
        collection(db, 'gift_cards'),
        where('senderUserId', '==', userId)
      );
      const sentGiftCardsSnapshot = await getDocs(sentGiftCardsQuery);
      console.log(`Found ${sentGiftCardsSnapshot.size} sent gift cards to delete`);
      
      for (const giftCardDoc of sentGiftCardsSnapshot.docs) {
        await deleteDoc(giftCardDoc.ref);
        deletionStats.sentGiftCards++;
      }

      // Delete gift cards where recipientUserId matches
      const receivedGiftCardsQuery = query(
        collection(db, 'gift_cards'),
        where('recipientUserId', '==', userId)
      );
      const receivedGiftCardsSnapshot = await getDocs(receivedGiftCardsQuery);
      console.log(`Found ${receivedGiftCardsSnapshot.size} received gift cards to delete`);
      
      for (const giftCardDoc of receivedGiftCardsSnapshot.docs) {
        await deleteDoc(giftCardDoc.ref);
        deletionStats.receivedGiftCards++;
      }

      // Delete credit activities where userId matches
      const creditActivitiesQuery = query(
        collection(db, 'creditActivities'),
        where('userId', '==', userId)
      );
      const creditActivitiesSnapshot = await getDocs(creditActivitiesQuery);
      console.log(`Found ${creditActivitiesSnapshot.size} credit activities to delete`);
      
      for (const creditActivityDoc of creditActivitiesSnapshot.docs) {
        await deleteDoc(creditActivityDoc.ref);
        deletionStats.creditActivities++;
      }

      // Create deleted user record - filter out undefined values
      const deletedUserData: any = {
        originalUserId: userId,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        emailVerified: userData.emailVerified || false,
        phoneVerified: userData.phoneVerified || false,
        deletedAt: Timestamp.now(),
        deletedBy: currentUser.id,
        deletedByName: `${currentUser.firstName || 'Unknown'} ${currentUser.lastName || 'User'}`,
        relatedDataDeleted: deletionStats
      };

      // Only add optional fields if they have values (not undefined)
      if (userData.role !== undefined) {
        deletedUserData.role = userData.role;
      }
      if (userData.systemRole !== undefined) {
        deletedUserData.systemRole = userData.systemRole;
      }
      if (userData.createdAt !== undefined) {
        deletedUserData.createdAt = userData.createdAt;
      }
      if (userData.phoneNumber !== undefined) {
        deletedUserData.phoneNumber = userData.phoneNumber;
      }
      if (userData.profilePictureURL !== undefined) {
        deletedUserData.profilePictureURL = userData.profilePictureURL;
      }
      if (deletionReason !== undefined) {
        deletedUserData.deletionReason = deletionReason;
      }

      // Add to deleted_users collection
      await addDoc(collection(db, 'deleted_users'), deletedUserData);
      console.log('Created deleted user record');

      // Finally, delete the user document from users collection
      await deleteDoc(doc(db, 'users', userId));
      console.log('Deleted user document from users collection');

      console.log(`Soft delete completed for user: ${userId}`, deletionStats);

    } catch (error) {
      console.error('Error in soft delete process:', error);
      throw error;
    }
  },

  async getDeletedUsers(): Promise<DeletedUserData[]> {
    try {
      const deletedUsersSnapshot = await getDocs(collection(db, 'deleted_users'));
      const deletedUsers: DeletedUserData[] = [];

      deletedUsersSnapshot.forEach((doc) => {
        deletedUsers.push({
          ...doc.data() as DeletedUserData,
          id: doc.id
        } as DeletedUserData);
      });

      // Sort by deletion date, newest first
      return deletedUsers.sort((a, b) => b.deletedAt.seconds - a.deletedAt.seconds);
    } catch (error) {
      console.error('Error fetching deleted users:', error);
      throw error;
    }
  }
};
